import type { AuthPhase, AuthUser, BillingStatus } from '../context/AuthContext';
import type { IntakeSummary } from '../types';
import { ROUTES } from '../navigation/routes';
import { pathForPhase } from '../navigation/phaseRedirect';
import { canOpenMentorEngagements } from './billingAccess';
import { canAccessIndividualPaywall } from './roleUtils';

/**
 * A mentor-profile user, read from the stored intake: the generated plan is
 * the mentor workspace, or the intake profile type is "mentor".
 */
export function isMentorProfile(
  summary: Pick<IntakeSummary, 'profile_type' | 'development_plan'> | null | undefined,
): boolean {
  if (!summary) return false;
  const kind = summary.development_plan?.plan_kind;
  return kind === 'mentor_workspace' || kind === 'mentor_plan' || summary.profile_type === 'mentor';
}

export interface PostCheckoutRouteInput {
  phase: AuthPhase;
  user: AuthUser | null | undefined;
  billingStatus: BillingStatus | null | undefined;
  mentorProfile: boolean;
}

/**
 * Where a completed checkout lands. Phase routing decides, except that a
 * mentor-profile user who now has Mentees (tier3) goes there instead of the
 * dashboard. Everyone else keeps the phase destination.
 */
export function resolvePostCheckoutRoute(input: PostCheckoutRouteInput): string {
  const { phase, user, billingStatus, mentorProfile } = input;
  const phasePath = pathForPhase(phase);
  if (phase !== 'authenticated' || !user || !mentorProfile) return phasePath;
  const entitled = canOpenMentorEngagements(billingStatus, {
    userType: user.user_type,
    requireBoundedB2CTrial: canAccessIndividualPaywall(user as any),
  });
  return entitled ? ROUTES.MENTOR_ENGAGEMENTS : phasePath;
}
